import { FC, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  FormControl,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { DealRequest, FactorBranch, IFactorsBranch } from '@/app/api/types';
import { useAssignFactorDealBranchMutation } from '@/app/api';
import { getFetchEror } from '@/app/helpers';
import { useNotification } from '@/shared/contexts/NotificationContext';
import UIAutocomplete from '@/shared/ui/ui-autocmplete';
import ModalTemplate from '@/shared/modal/modal-template';
import { UIFormLabel } from '@/shared/ui/ui-form-label';
import { InfoModal } from '@/shared/modals/info-modal';
import { InfoModalRef } from '@/shared/modals/info-modal/types';
import CircleTick from '@/app/assets/icons/circle-tick.svg?react';

interface Props {
  data: DealRequest;
  factorBranch?: FactorBranch | null;
  branches: IFactorsBranch[];
  isBranchesLoading?: boolean;
}

export const DealBranchAssignment: FC<Props> = ({
  data,
  factorBranch,
  branches,
  isBranchesLoading,
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const successModalRef = useRef<InfoModalRef>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<IFactorsBranch | null>(null);

  const [assignBranch, { isLoading: isAssigning }] =
    useAssignFactorDealBranchMutation();

  const isChangeMode = data.branchStage === 'awaitingBranchApproval';
  const currentBranchName = factorBranch?.name ?? null;

  const title = useMemo(
    () =>
      isChangeMode
        ? t('branch_assignment.change_title')
        : t('branch_assignment.assign_title'),
    [isChangeMode, t]
  );

  const openModal = () => {
    setSelected(null);
    setIsModalOpen(true);
  };

  const handleConfirm = async () => {
    if (!selected) return;

    try {
      await assignBranch({
        id: data.unique,
        branchId: selected.unique,
      }).unwrap();

      setIsModalOpen(false);
      successModalRef.current?.open();
    } catch (error) {
      showNotification(getFetchEror(error), 'error');
    }
  };

  return (
    <>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{ mt: 2 }}
      >
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <Typography fontSize={14} fontWeight={500} color='text.secondary'>
            {t('branch_assignment.branch')}:
          </Typography>

          {currentBranchName ? (
            <Typography fontSize={14} fontWeight={600} color='text.primary'>
              {currentBranchName}
            </Typography>
          ) : (
            <Typography fontSize={14} fontWeight={600} color='error.main'>
              ✕ {t('branch_assignment.not_selected')}
            </Typography>
          )}
        </Stack>

        <Button onClick={openModal}>
          {isChangeMode
            ? t('branch_assignment.change_branch')
            : t('branch_assignment.assign_branch')}
        </Button>
      </Stack>

      <Dialog
        open={isModalOpen}
        PaperProps={{ sx: { maxWidth: 700, width: '100%' } }}
      >
        <ModalTemplate
          title={title}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleConfirm}
          submitBtnText={t('confirm')}
          disabled={!selected || isAssigning}
          dialogBottomContent={
            !isChangeMode ? (
              <Typography mt={0.5} variant='subtitle2' color='text.secondary'>
                {t('branch_assignment.assign_subtitle')}
              </Typography>
            ) : undefined
          }
        >
          <Stack spacing={2}>
            {isChangeMode && (
              <FormControl fullWidth>
                <UIFormLabel sx={{ color: '#0B1370' }}>
                  {t('branch_assignment.current_branch')}:
                </UIFormLabel>
                <TextField value={currentBranchName ?? ''} disabled />
              </FormControl>
            )}

            <FormControl fullWidth required>
              <UIFormLabel sx={{ color: '#0B1370' }}>
                {t('branch_assignment.new_branch')}:
              </UIFormLabel>
              <UIAutocomplete
                value={selected}
                options={branches}
                loading={isBranchesLoading}
                getOptionLabel={(option) => option.branchName}
                isOptionEqualToValue={(option, value) =>
                  option.unique === value.unique
                }
                TextFieldProps={{
                  placeholder: t('branch_assignment.select_branch'),
                }}
                onChange={(_event, value) => setSelected(value)}
              />
            </FormControl>
          </Stack>
        </ModalTemplate>
      </Dialog>

      <Box>
        <InfoModal
          ref={successModalRef}
          title={
            isChangeMode
              ? t('branch_assignment.success_update_title')
              : t('branch_assignment.success_assign_title')
          }
          subtitle={t('branch_assignment.success_subtitle')}
          Icon={CircleTick}
        />
      </Box>
    </>
  );
};
